import type { CanvasDoc } from '../store/types';
import { encodeDoc, decodeDoc } from '../io/serialize';
import { assetStore } from './assetStore';
import { collectAssetIds } from './resolveAssets';

interface AssetBundle {
  version: 1;
  doc: unknown;
  assets: Record<string, string>; // assetId → base64 (no data: prefix)
}

function bytesToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function base64ToBytes(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/** True if `text` parses as an asset bundle (vs a plain doc). */
export function isBundle(text: string): boolean {
  try {
    const p = JSON.parse(text);
    return p && typeof p === 'object' && p.version === 1 && 'doc' in p && 'assets' in p;
  } catch {
    return false;
  }
}

/** Serialize the doc + every referenced blob into one JSON bundle string. */
export async function exportBundle(doc: CanvasDoc): Promise<string> {
  const ids = collectAssetIds(doc);
  const assets: Record<string, string> = {};
  for (const id of ids) {
    const bytes = await assetStore.getBytes(id);
    if (bytes) assets[id] = bytesToBase64(bytes);
  }
  // encodeDoc/decodeDoc are the canonical doc (de)serializers — reuse them so
  // the bundle's doc benefits from the same migration path on import.
  const bundle: AssetBundle = { version: 1, doc: JSON.parse(encodeDoc(doc)), assets };
  return JSON.stringify(bundle);
}

/** Rehydrate blobs into IndexedDB and return the decoded doc. */
export async function importBundle(text: string): Promise<CanvasDoc> {
  const bundle = JSON.parse(text) as AssetBundle;
  for (const [id, b64] of Object.entries(bundle.assets)) {
    if (!(await assetStore.has(id))) {
      // putBytes re-hashes; that matches the content-hash id, preserving asset:// refs.
      await assetStore.putBytes(base64ToBytes(b64));
    }
  }
  return decodeDoc(JSON.stringify(bundle.doc));
}
